import React, {useEffect, useState} from 'react';
import {App, Badge, Button, Card, Empty, InputNumber, Table, Tag, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useNavigate} from 'react-router-dom';
import {EyeOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, ReloadOutlined} from '@ant-design/icons';
import {strategyTaskService} from '../../services/strategyTaskService';
import type {StrategyTask, StrategyTaskDetail, TradeRecord} from '../../types/strategyTask';
import styles from './StrategyTasksPage.module.css';
import {useAuth} from '../../contexts/AuthContext';

const {Title, Text} = Typography;

/**
 * StrategyTasksPage
 * 策略任务管理页面
 * 展示实盘交易KPI、策略实时状态、成交记录、月度盈亏和风险计算器
 */
const StrategyTasksPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const {user} = useAuth();
    const isAdmin = user?.roles?.some(role => role.toUpperCase() === 'ADMIN');
    // 状态管理
    const [tasks, setTasks] = useState<StrategyTask[]>([]);
    const [recentTrades, setRecentTrades] = useState<TradeRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [riskCapital, setRiskCapital] = useState(25000);
    const [riskDrawdown, setRiskDrawdown] = useState(18);
    const [riskAlloc, setRiskAlloc] = useState(60);
    const [mode, setMode] = useState<'live' | 'sim'>('live'); // 实盘/模拟切换
    const [filter, setFilter] = useState<'all' | 'running' | 'paused'>('all'); // 筛选状态
    const [selectedTask, setSelectedTask] = useState<StrategyTask | null>(null);
    const [selectedTaskDetail, setSelectedTaskDetail] = useState<StrategyTaskDetail | null>(null);
    const [overviewDetail, setOverviewDetail] = useState<StrategyTaskDetail | null>(null);
    const [detailTrades, setDetailTrades] = useState<TradeRecord[]>([]);
    const [detailLogs, setDetailLogs] = useState<string>('');
    const [detailLoading, setDetailLoading] = useState(false);

    /**
     * 加载所有任务数据
     */
    const loadData = async () => {
        setLoading(true);
        try {
            const [taskList, tradeList] = await Promise.all([
                strategyTaskService.getAllTasks(),
                strategyTaskService.getRecentTrades(10)
            ]);
            setTasks(taskList);
            setRecentTrades(tradeList);
        } catch (error) {
            messageApi.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        const shouldPoll = tasks.some(task => task.status === 1) || !!selectedTask;
        if (!shouldPoll) {
            return;
        }

        const timer = window.setInterval(() => {
            loadData();
            if (selectedTask?.id) {
                openTaskDetail(selectedTask.id);
            }
        }, 10000);

        return () => window.clearInterval(timer);
    }, [tasks, selectedTask?.id]);

    /**
     * 暂停所有任务
     */
    const handlePauseAll = async () => {
        try {
            const count = await strategyTaskService.pauseAllTasks();
            messageApi.success(`已暂停 ${count} 个策略`);
            loadData();
        } catch (error: any) {
            messageApi.error(error.message || '暂停失败');
        }
    };

    /**
     * 根据筛选状态过滤任务
     */
    const getFilteredTasks = () => {
        const modeTasks = tasks.filter(task => {
            const normalizedMode = (task.runMode || 'DRY_RUN').toUpperCase();
            return mode === 'live' ? normalizedMode === 'LIVE' : normalizedMode === 'DRY_RUN';
        });

        switch (filter) {
            case 'running':
                return modeTasks.filter(t => t.status === 1);
            case 'paused':
                return modeTasks.filter(t => t.status === 0 || t.status === 2);
            default:
                return modeTasks;
        }
    };

    const filteredTasks = getFilteredTasks();
    const overviewTask = selectedTask
        ? (tasks.find(task => task.id === selectedTask.id) || selectedTask)
        : (filteredTasks[0] || null);
    const activeOverviewDetail = selectedTask?.id === overviewTask?.id ? selectedTaskDetail : overviewDetail;

    useEffect(() => {
        if (!filteredTasks.length) {
            setSelectedTask(null);
            setSelectedTaskDetail(null);
            return;
        }
        if (!selectedTask || !filteredTasks.some(task => task.id === selectedTask.id)) {
            openTaskDetail(filteredTasks[0].id);
        }
    }, [filteredTasks.length, filteredTasks[0]?.id]);

    useEffect(() => {
        if (!overviewTask?.id || selectedTask?.id === overviewTask.id) {
            if (!overviewTask?.id) {
                setOverviewDetail(null);
            }
            return;
        }

        let cancelled = false;
        strategyTaskService.getTaskDetail(overviewTask.id, 5, 0)
            .then(detail => {
                if (!cancelled) {
                    setOverviewDetail(detail);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOverviewDetail(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [overviewTask?.id, selectedTask?.id]);

    /**
     * 暂停单个任务
     */
    const handlePauseTask = async (id: number) => {
        try {
            await strategyTaskService.pauseTask(id);
            messageApi.success('策略已暂停');
            loadData();
        } catch (error: any) {
            messageApi.error(error.message || '暂停失败');
        }
    };

    /**
     * 恢复单个任务
     */
    const handleResumeTask = async (id: number) => {
        try {
            await strategyTaskService.resumeTask(id);
            messageApi.success('策略已恢复');
            loadData();
        } catch (error: any) {
            messageApi.error(error.message || '恢复失败');
        }
    };

    const handleRestartTask = async (id: number) => {
        try {
            await strategyTaskService.restartTask(id);
            messageApi.success('策略已重启');
            loadData();
            if (selectedTask?.id === id) {
                openTaskDetail(id);
            }
        } catch (error: any) {
            messageApi.error(error.message || '重启失败');
        }
    };

    const getStatusBadge = (task: StrategyTask) => {
        if (task.status === 1) {
            return <Badge status="processing" text="运行中"/>;
        }
        if (task.status === 2) {
            return <Badge status="error" text="异常"/>;
        }
        return <Badge status="default" text="已停止"/>;
    };

    const openTaskDetail = async (id: number) => {
        setDetailLoading(true);
        try {
            const detail: StrategyTaskDetail | null = await strategyTaskService.getTaskDetail(id, 10, 80);
            setSelectedTaskDetail(detail);
            setSelectedTask(detail?.task || null);
            setDetailTrades(detail?.recentTrades || []);
            setDetailLogs(detail?.instanceLogs || '');
        } catch (error) {
            messageApi.error('加载任务详情失败');
        } finally {
            setDetailLoading(false);
        }
    };

    /**
     * 计算风险指标
     */
    const riskExposed = (riskCapital * riskAlloc) / 100;
    const riskMaxLoss = (riskExposed * riskDrawdown) / 100;
    const riskRemaining = riskCapital - riskMaxLoss;

    // 成交记录表格列定义
    const tradeColumns: ColumnsType<TradeRecord> = [
        {
            title: '时间',
            dataIndex: 'tradeTime',
            key: 'tradeTime',
            render: (text: string) => (
                <Text type="secondary" style={{fontSize: '0.72rem'}}>
                    {new Date(text).toLocaleString('zh-CN')}
                </Text>
            )
        },
        {
            title: '交易对',
            dataIndex: 'tradingPair',
            key: 'tradingPair'
        },
        {
            title: '方向',
            dataIndex: 'side',
            key: 'side',
            render: (side: string) => (
                <Tag color={'BUY' === side ? 'teal' : 'coral'}
                     style={{
                         background: 'BUY' === side ? 'rgba(0,200,150,0.1)' : 'rgba(255,107,107,0.1)',
                         borderColor: 'BUY' === side ? 'rgba(0,200,150,0.3)' : 'rgba(255,107,107,0.3)',
                         color: 'BUY' === side ? 'var(--pt-bull)' : 'var(--pt-bear)',
                         fontWeight: 600,
                         fontSize: '0.72rem'
                     }}>
                    {'BUY' === side ? '买入' : '卖出'}
                </Tag>
            )
        },
        {
            title: '盈亏',
            dataIndex: 'pnl',
            key: 'pnl',
            align: 'right',
            render: (pnl: number, record: TradeRecord) => {
                if ('SELL' !== record.side || null == pnl) {
                    return <Text type="secondary">持仓中</Text>;
                }
                const isProfit = pnl >= 0;
                return (
                    <Text style={{
                        color: isProfit ? 'var(--pt-bull)' : 'var(--pt-bear)',
                        fontFamily: "'IBM Plex Mono', monospace"
                    }}>
                        {isProfit ? '+' : ''}{pnl.toFixed(2)}
                    </Text>
                );
            }
        }
    ];

    const renderTaskDetailPanel = () => {
        if (!selectedTask) {
            return (
                <Card className={styles.panel}>
                    <div style={{padding: '0.5rem'}}>
                        <Empty description="选择左侧任务查看详情"/>
                    </div>
                </Card>
            );
        }

        return (
            <Card className={styles.panel} style={{position: 'sticky', top: 16}}>
                <div className={styles['panel-header']}>
                    <div className={styles['panel-title']}>任务详情 · {selectedTask.taskName}</div>
                    <div style={{display: 'flex', gap: 8}}>
                        {selectedTask.freqtradeInstanceId && (
                            <Button size="small"
                                    onClick={() => navigate(`/strategy-execution/monitor/${selectedTask.freqtradeInstanceId}`)}>
                                监控
                            </Button>
                        )}
                        <Button size="small" icon={<ReloadOutlined/>} onClick={() => openTaskDetail(selectedTask.id)}
                                loading={detailLoading}>
                            刷新
                        </Button>
                    </div>
                </div>
                <div style={{padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: 16}}>
                    <div className={styles.detailGrid}>
                        <DetailItem label="任务状态" value={getStatusBadge(selectedTask)}/>
                        <DetailItem label="运行模式"
                                    value={(selectedTask.runMode || 'DRY_RUN') === 'LIVE' ? '实盘' : '模拟'}/>
                        <DetailItem label="交易对" value={selectedTask.tradingPair}/>
                        <DetailItem label="实例ID"
                                    value={selectedTask.freqtradeInstanceId ? `#${selectedTask.freqtradeInstanceId}` : '未绑定'}/>
                        <DetailItem label="策略配置" value={selectedTask.strategyConfigId || '-'}/>
                        <DetailItem label="账户连接" value={selectedTask.apiKeyId || '-'}/>
                        <DetailItem label="Freqtrade配置" value={selectedTask.freqtradeConfigId || '-'}/>
                        <DetailItem label="最近同步"
                                    value={selectedTask.lastSyncAt ? new Date(selectedTask.lastSyncAt).toLocaleString('zh-CN') : '暂无'}/>
                    </div>

                    <div className={styles.detailGrid}>
                        <DetailItem label="总净值" value={`${(selectedTask.totalEquity || 0).toFixed(2)} USDT`}/>
                        <DetailItem label="今日盈亏" value={
                            <span className={selectedTask.todayPnl >= 0 ? styles.up : styles.down}>
                                {selectedTask.todayPnl >= 0 ? '+' : ''}{(selectedTask.todayPnl || 0).toFixed(2)} USDT
                            </span>
                        }/>
                        <DetailItem label="本月信号" value={`${selectedTask.monthSignals || 0} 次`}/>
                        <DetailItem label="当前回撤" value={`${(selectedTask.currentDrawdown || 0).toFixed(2)}%`}/>
                        <DetailItem label="止损" value={`${selectedTask.stopLossPercent ?? '-'}%`}/>
                        <DetailItem label="止盈" value={`${selectedTask.takeProfitPercent ?? '-'}%`}/>
                    </div>

                    {selectedTaskDetail?.profitSummary && (
                        <div className={styles.detailGrid}>
                            <DetailItem
                                label="累计收益"
                                value={
                                    <span
                                        className={(selectedTaskDetail.profitSummary.profitAll || 0) >= 0 ? styles.up : styles.down}>
                                        {(selectedTaskDetail.profitSummary.profitAll || 0) >= 0 ? '+' : ''}
                                        {(selectedTaskDetail.profitSummary.profitAll || 0).toFixed(4)}
                                    </span>
                                }
                            />
                            <DetailItem label="收益率"
                                        value={`${((selectedTaskDetail.profitSummary.profitRatio || 0) * 100).toFixed(2)}%`}/>
                            <DetailItem label="成交总数" value={`${selectedTaskDetail.profitSummary.tradeCount || 0}`}/>
                            <DetailItem label="盈利次数"
                                        value={`${selectedTaskDetail.profitSummary.winningTrades || 0}`}/>
                        </div>
                    )}

                    <div>
                        <div className={styles['panel-title']} style={{marginBottom: 12}}>当前持仓</div>
                        {!selectedTaskDetail?.openPositions?.length ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前无持仓"/>
                        ) : (
                            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                {selectedTaskDetail.openPositions.map(position => (
                                    <div key={`${position.tradeId}-${position.pair}`} className={styles.detailItem}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', gap: 12}}>
                                            <div>
                                                <div className={styles.detailValue}>{position.pair}</div>
                                                <div className={styles.detailLabel}>
                                                    开仓 {position.openDate ? new Date(position.openDate).toLocaleString('zh-CN') : '暂无'}
                                                </div>
                                            </div>
                                            <div style={{textAlign: 'right'}}>
                                                <div className={(position.profit || 0) >= 0 ? styles.up : styles.down}>
                                                    {(position.profit || 0) >= 0 ? '+' : ''}{(position.profit || 0).toFixed(4)}
                                                </div>
                                                <div className={styles.detailLabel}>
                                                    {((position.profitPct || 0) * 100).toFixed(2)}%
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3,1fr)',
                                            gap: 8,
                                            marginTop: 8
                                        }}>
                                            <div>
                                                <div className={styles.detailLabel}>数量</div>
                                                <div
                                                    className={styles.detailValue}>{(position.amount || 0).toFixed(4)}</div>
                                            </div>
                                            <div>
                                                <div className={styles.detailLabel}>开仓价</div>
                                                <div
                                                    className={styles.detailValue}>{(position.openRate || 0).toFixed(4)}</div>
                                            </div>
                                            <div>
                                                <div className={styles.detailLabel}>现价</div>
                                                <div
                                                    className={styles.detailValue}>{(position.currentRate || 0).toFixed(4)}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedTask.lastError && (
                        <div>
                            <div className={styles['panel-title']} style={{marginBottom: 12}}>最近错误</div>
                            <div className={styles.logBlock}>{selectedTask.lastError}</div>
                        </div>
                    )}

                    <div>
                        <div className={styles['panel-header']}>
                            <div className={styles['panel-title']}>最近成交</div>
                            <Text type="secondary">{detailTrades.length} 条</Text>
                        </div>
                        <Table
                            rowKey="id"
                            columns={tradeColumns}
                            dataSource={detailTrades}
                            size="small"
                            pagination={false}
                            locale={{emptyText: '该任务暂无成交记录'}}
                        />
                    </div>

                    <div>
                        <div className={styles['panel-header']}>
                            <div className={styles['panel-title']}>实例日志预览</div>
                            <Text
                                type="secondary">{selectedTask.freqtradeInstanceId ? '最近 80 行' : '未绑定实例'}</Text>
                        </div>
                        <div className={styles.logBlock}>
                            {detailLogs || '暂无实例日志'}
                        </div>
                    </div>

                    <div style={{display: 'flex', gap: 8}}>
                        {selectedTask.status === 1 ? (
                            <Button icon={<PauseCircleOutlined/>} onClick={() => handlePauseTask(selectedTask.id)}>
                                停止任务
                            </Button>
                        ) : (
                            <Button type="primary" icon={<PlayCircleOutlined/>}
                                    onClick={() => handleResumeTask(selectedTask.id)}>
                                启动任务
                            </Button>
                        )}
                        <Button icon={<ReloadOutlined/>} onClick={() => handleRestartTask(selectedTask.id)}>
                            重启任务
                        </Button>
                    </div>
                </div>
            </Card>
        );
    };

    return (
        <div style={{position: 'relative', flex: 1, display: 'flex', flexDirection: 'column'}}>
            {!isAdmin && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'inherit'
                }}>
                    <div style={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: '3rem',
                        fontWeight: 500,
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                    }}>
                        功能内测中...
                    </div>
                </div>
            )}
            <div style={{flex: 1, overflowY: 'auto'}}>
                {/* 页面头部 */}
                <div className={styles.dashHeader}>
                    <div>
                        <Title level={2} style={{margin: 0, fontSize: '1.5rem'}}>
                            {mode === 'live' ? '实盘策略' : '模拟策略'}
                        </Title>
                        <Text style={{display: 'flex', alignItems: 'center', gap: 6}}>
                            {mode === 'live' ? (
                                <>
                                <span style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: 'var(--pt-bull)',
                                    animation: 'pulse 2s infinite',
                                    display: 'inline-block'
                                }}/>
                                    实盘运行中
                                </>
                            ) : (
                                <>
                                <span style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: 'var(--pt-primary)',
                                    animation: 'pulse 2s infinite',
                                    display: 'inline-block'
                                }}/>
                                    模拟盘运行中
                                </>
                            )}
                        </Text>
                    </div>
                    <div className={styles.headerActions}>
                        {/* 实盘/模拟切换 */}
                        <div className={styles.toggleGroup}>
                            <button
                                className={`${styles.toggleBtn} ${mode === 'live' ? styles.active : ''}`}
                                onClick={() => setMode('live')}
                                title="实盘交易"
                            >
                                实盘
                            </button>
                            <button
                                className={`${styles.toggleBtn} ${mode === 'sim' ? styles.active : ''}`}
                                onClick={() => setMode('sim')}
                                title="模拟交易"
                            >
                                模拟
                            </button>
                        </div>

                        {/* 筛选按钮 */}
                        <div className={styles.filterBar}>
                            <button
                                className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
                                onClick={() => setFilter('all')}
                            >
                                全部
                            </button>
                            <button
                                className={`${styles.filterBtn} ${filter === 'running' ? styles.active : ''}`}
                                onClick={() => setFilter('running')}
                            >
                                运行中
                            </button>
                            <button
                                className={`${styles.filterBtn} ${filter === 'paused' ? styles.active : ''}`}
                                onClick={() => setFilter('paused')}
                            >
                                已暂停
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                    {/* KPI卡片 */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5,1fr)',
                        gap: '1rem'
                    }}>
                        <KpiCard
                            label="任务总净值"
                            value={`${filteredTasks.reduce((sum, t) => sum + (t.totalEquity || 0), 0).toLocaleString()}`}
                            suffix="USDT"
                            borderColor="var(--pt-bull)"
                        />
                        <KpiCard
                            label="今日盈亏"
                            value={`${filteredTasks.reduce((sum, t) => sum + (t.todayPnl || 0), 0).toFixed(2)}`}
                            suffix="USDT"
                            borderColor="var(--pt-primary)"
                            changeColor="up"
                        />
                        <KpiCard
                            label="运行中任务"
                            value={`${filteredTasks.filter(t => t.status === 1).length}`}
                            borderColor="var(--pt-blue)"
                            change={`${mode === 'live' ? '实盘' : '模拟'}模式`}
                        />
                        <KpiCard
                            label="本月信号"
                            value={`${filteredTasks.reduce((sum, t) => sum + (t.monthSignals || 0), 0)} 次`}
                            borderColor="var(--pt-purple)"
                            change={
                                <>
                                    <span
                                        className={styles.up}>{filteredTasks.reduce((sum, t) => sum + (t.monthWins || 0), 0)} 盈</span>
                                    {' · '}
                                    <span
                                        className={styles.down}>{filteredTasks.reduce((sum, t) => sum + (t.monthLosses || 0), 0)} 亏</span>
                                </>
                            }
                        />
                        <KpiCard
                            label="当前回撤"
                            value={`${filteredTasks.reduce((sum, t) => sum + (t.currentDrawdown || 0), 0).toFixed(1)}%`}
                            borderColor="var(--pt-bull)"
                            change="安全 · 止损 -12%"
                            changeColor="teal"
                        />
                    </div>

                    {/* 实时走势 + 策略状态 */}
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem'}}>
                        {/* 当前任务概览 */}
                        <Card className={styles.panel}>
                            <div className={styles['panel-header']}>
                                <div className={styles['panel-title']}>
                                    {overviewTask ? `${overviewTask.taskName} 概览` : '任务概览'}
                                </div>
                                {overviewTask ? getStatusBadge(overviewTask) : <Text type="secondary">暂无任务</Text>}
                            </div>
                            {!overviewTask ? (
                                <div style={{padding: '0.5rem'}}>
                                    <Empty description="当前模式下暂无任务"/>
                                </div>
                            ) : (
                                <div style={{padding: '0.5rem'}}>
                                    <div className={styles.detailGrid} style={{marginBottom: '1rem'}}>
                                        <DetailItem label="交易对" value={overviewTask.tradingPair}/>
                                        <DetailItem label="运行模式"
                                                    value={(overviewTask.runMode || 'DRY_RUN') === 'LIVE' ? '实盘' : '模拟'}/>
                                        <DetailItem label="策略配置" value={overviewTask.strategyConfigId || '-'}/>
                                        <DetailItem label="实例ID"
                                                    value={overviewTask.freqtradeInstanceId ? `#${overviewTask.freqtradeInstanceId}` : '未绑定'}/>
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4,1fr)',
                                        gap: '.75rem',
                                        marginTop: '1rem'
                                    }}>
                                        <MiniStat
                                            label="总净值"
                                            value={`${(overviewTask.totalEquity || 0).toFixed(2)}`}
                                            color="var(--pt-bull)"
                                        />
                                        <MiniStat
                                            label="今日盈亏"
                                            value={`${overviewTask.todayPnl >= 0 ? '+' : ''}${(overviewTask.todayPnl || 0).toFixed(2)}`}
                                            color={overviewTask.todayPnl >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}
                                        />
                                        <MiniStat
                                            label="当前持仓"
                                            value={`${activeOverviewDetail?.openPositions?.length || 0}`}
                                            color="var(--pt-primary)"
                                        />
                                        <MiniStat
                                            label="收益率"
                                            value={`${(((activeOverviewDetail?.profitSummary?.profitRatio || 0)) * 100).toFixed(2)}%`}
                                            color={((activeOverviewDetail?.profitSummary?.profitRatio || 0)) >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}
                                        />
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4,1fr)',
                                        gap: '.75rem',
                                        marginTop: '.75rem'
                                    }}>
                                        <MiniStat
                                            label="成交总数"
                                            value={`${activeOverviewDetail?.profitSummary?.tradeCount || 0}`}
                                        />
                                        <MiniStat
                                            label="盈利次数"
                                            value={`${activeOverviewDetail?.profitSummary?.winningTrades || 0}`}
                                            color="var(--pt-bull)"
                                        />
                                        <MiniStat
                                            label="累计收益"
                                            value={`${(activeOverviewDetail?.profitSummary?.profitAll || 0) >= 0 ? '+' : ''}${(activeOverviewDetail?.profitSummary?.profitAll || 0).toFixed(4)}`}
                                            color={(activeOverviewDetail?.profitSummary?.profitAll || 0) >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}
                                        />
                                        <MiniStat
                                            label="当前回撤"
                                            value={`${(overviewTask.currentDrawdown || 0).toFixed(2)}%`}
                                            color={(overviewTask.currentDrawdown || 0) > 0 ? 'var(--pt-bear)' : 'var(--pt-text-primary)'}
                                        />
                                    </div>
                                    <div style={{
                                        marginTop: '1rem',
                                        padding: '0.875rem 1rem',
                                        background: 'var(--pt-bg-hover)',
                                        border: '1px solid var(--pt-border-subtle)',
                                        borderRadius: 10
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            flexWrap: 'wrap'
                                        }}>
                                            <Text
                                                type="secondary">最近同步: {overviewTask.lastSyncAt ? new Date(overviewTask.lastSyncAt).toLocaleString('zh-CN') : '暂无'}</Text>
                                            <Text type="secondary">止损 {overviewTask.stopLossPercent ?? '-'}% ·
                                                止盈 {overviewTask.takeProfitPercent ?? '-'}%</Text>
                                        </div>
                                        {!!activeOverviewDetail?.openPositions?.length && (
                                            <div style={{marginTop: '0.75rem'}}>
                                                <Text type="secondary">
                                                    当前持仓: {activeOverviewDetail.openPositions.map(position => position.pair).join(' / ')}
                                                </Text>
                                            </div>
                                        )}
                                        {overviewTask.lastError && (
                                            <div style={{marginTop: '0.75rem'}}>
                                                <Tag color="error">最近错误</Tag>
                                                <Text type="secondary">{overviewTask.lastError}</Text>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* 策略实时状态 */}
                        <Card className={styles.panel}>
                            <div className={styles['panel-header']}>
                                <div className={styles['panel-title']}>策略实时状态</div>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '.875rem'}}>
                                {filteredTasks.map(task => (
                                    <div key={task.id}
                                         style={{
                                             background: 'var(--pt-bg-card)',
                                             border: '1px solid var(--pt-border)',
                                             borderRadius: 10,
                                             padding: '.875rem'
                                         }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '.4rem'
                                            }}>
                                            <Text strong>{task.taskName}</Text>
                                            {getStatusBadge(task)}
                                        </div>
                                        <Text type="secondary"
                                              style={{fontSize: '0.78rem', marginBottom: '.625rem', display: 'block'}}>
                                            {task.tradingPair} · {(task.runMode || 'DRY_RUN') === 'LIVE' ? '实盘' : '模拟'} ·
                                            净值 {task.totalEquity?.toFixed(2)}
                                        </Text>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '0.78rem'
                                        }}>
                                            <Text type="secondary">止损 {task.stopLossPercent}%</Text>
                                            <Text type="secondary">今日 {task.todayPnl?.toFixed(2)}</Text>
                                        </div>
                                        <div style={{display: 'flex', gap: 8, marginTop: '.75rem'}}>
                                            {task.status === 1 ? (
                                                <Button size="small" icon={<PauseCircleOutlined/>}
                                                        onClick={() => handlePauseTask(task.id)}>
                                                    停止
                                                </Button>
                                            ) : (
                                                <Button size="small" type="primary" icon={<PlayCircleOutlined/>}
                                                        onClick={() => handleResumeTask(task.id)}>
                                                    启动
                                                </Button>
                                            )}
                                            {task.freqtradeInstanceId && task.status === 1 && (
                                                <Button size="small"
                                                        onClick={() => navigate(`/strategy-execution/monitor/${task.freqtradeInstanceId}`)}>
                                                    监控
                                                </Button>
                                            )}
                                            <Button size="small" icon={<EyeOutlined/>}
                                                    onClick={() => openTaskDetail(task.id)}>
                                                详情
                                            </Button>
                                        </div>
                                        <div style={{
                                            height: 4,
                                            background: 'var(--pt-bg-hover)',
                                            borderRadius: 2,
                                            marginTop: '.5rem',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: '73%',
                                                height: '100%',
                                                background: 'var(--pt-bull)',
                                                borderRadius: 2
                                            }}/>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    style={{
                                        width: '100%',
                                        background: 'none',
                                        borderColor: 'var(--pt-border)',
                                        color: 'var(--pt-text-secondary)',
                                        padding: '0.625rem',
                                        borderRadius: 8,
                                        fontSize: '0.82rem'
                                    }}
                                    icon={<PlusOutlined/>}
                                    onClick={() => navigate('/strategy/list')}
                                >
                                    添加策略
                                </Button>
                            </div>
                        </Card>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 520px',
                        gap: '1.5rem',
                        alignItems: 'start'
                    }}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                            <Card className={styles.panel}>
                                <div className={styles['panel-header']}>
                                    <div className={styles['panel-title']}>任务列表</div>
                                    <Text type="secondary">{filteredTasks.length} 个任务</Text>
                                </div>
                                <Table
                                    rowKey="id"
                                    loading={loading}
                                    size="small"
                                    pagination={false}
                                    dataSource={filteredTasks}
                                    locale={{emptyText: '当前筛选条件下暂无任务'}}
                                    scroll={{x: 'max-content'}}
                                    columns={[
                                        {
                                            title: '任务',
                                            key: 'taskName',
                                            render: (_, record: StrategyTask) => (
                                                <div
                                                    onClick={() => openTaskDetail(record.id)}
                                                    style={{cursor: 'pointer'}}
                                                >
                                                    <div style={{fontWeight: 600}}>{record.taskName}</div>
                                                    <Text type="secondary" style={{fontSize: '0.75rem'}}>
                                                        {record.tradingPair} · {(record.runMode || 'DRY_RUN') === 'LIVE' ? '实盘' : '模拟'}
                                                    </Text>
                                                </div>
                                            )
                                        },
                                        {
                                            title: '状态',
                                            key: 'status',
                                            width: 120,
                                            render: (_, record: StrategyTask) => getStatusBadge(record)
                                        },
                                        {
                                            title: '今日盈亏',
                                            dataIndex: 'todayPnl',
                                            align: 'right',
                                            width: 120,
                                            render: (value: number) => (
                                                <Text className={value >= 0 ? styles.up : styles.down}>
                                                    {value >= 0 ? '+' : ''}{(value || 0).toFixed(2)}
                                                </Text>
                                            )
                                        },
                                        {
                                            title: '实例',
                                            key: 'instance',
                                            width: 120,
                                            render: (_, record: StrategyTask) => record.freqtradeInstanceId ? (
                                                <Text className={styles.mono}>#{record.freqtradeInstanceId}</Text>
                                            ) : (
                                                <Text type="secondary">未绑定</Text>
                                            )
                                        },
                                        {
                                            title: '操作',
                                            key: 'actions',
                                            width: 200,
                                            render: (_, record: StrategyTask) => (
                                                <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                                                    <Button size="small" icon={<EyeOutlined/>}
                                                            onClick={() => openTaskDetail(record.id)}>
                                                        详情
                                                    </Button>
                                                    {record.status === 1 ? (
                                                        <Button size="small" icon={<PauseCircleOutlined/>}
                                                                onClick={() => handlePauseTask(record.id)}>
                                                            停止
                                                        </Button>
                                                    ) : (
                                                        <Button size="small" type="primary" icon={<PlayCircleOutlined/>}
                                                                onClick={() => handleResumeTask(record.id)}>
                                                            启动
                                                        </Button>
                                                    )}
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            {/* 成交记录 */}
                            <Card className={styles.panel}>
                                <div className={styles['panel-header']}>
                                    <div className={styles['panel-title']}>近期成交记录</div>
                                </div>
                                <Table
                                    columns={tradeColumns}
                                    dataSource={recentTrades}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={false}
                                    size="small"
                                    locale={{emptyText: '暂无成交记录'}}
                                />
                            </Card>

                            {/* 月度盈亏分布 */}
                            <Card className={styles.panel}>
                                <div className={styles['panel-header']}>
                                    <div className={styles['panel-title']}>月度盈亏分布</div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '.625rem',
                                    paddingTop: '.5rem'
                                }}>
                                    {parseMonthlyReturns(filteredTasks).map((item, idx) => (
                                        <div key={idx} style={{display: 'flex', alignItems: 'center', gap: '.875rem'}}>
                                            <Text type="secondary"
                                                  style={{fontSize: '0.78rem', width: 28, textAlign: 'right'}}>
                                                {item.month}
                                            </Text>
                                            <div style={{
                                                flex: 1,
                                                height: 22,
                                                background: 'var(--pt-bg-hover)',
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                <div style={{
                                                    width: `${Math.min(Math.abs(item.value) * 10, 100)}%`,
                                                    height: '100%',
                                                    background: item.value >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                                    borderRadius: 3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: item.value >= 0 ? 'flex-end' : 'flex-start',
                                                    paddingRight: item.value >= 0 ? 6 : 0,
                                                    paddingLeft: item.value < 0 ? 6 : 0
                                                }}>
                                                    <Text style={{
                                                        fontSize: '0.72rem',
                                                        color: item.value >= 0 ? '#000' : '#fff',
                                                        fontWeight: 600,
                                                        fontFamily: "'IBM Plex Mono', monospace"
                                                    }}>
                                                        {item.value >= 0 ? '+' : ''}{item.value}%
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{
                                    marginTop: '1rem',
                                    paddingTop: '1rem',
                                    borderTop: '1px solid var(--pt-border-subtle)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '0.82rem'
                                }}>
                                    <Text type="secondary">5个月累计</Text>
                                    <Text className={styles.up}
                                          style={{fontFamily: "'IBM Plex Mono', monospace"}}>
                                        +15.7%
                                    </Text>
                                </div>
                            </Card>
                        </div>

                        {renderTaskDetailPanel()}
                    </div>

                    {/* 风险计算器 */}
                    <Card className={styles.panel}
                          style={{background: 'linear-gradient(135deg,var(--pt-bg-panel),rgba(240,180,41,0.04))'}}>
                        <div className={styles['panel-header']}>
                            <div className={styles['panel-title']}>实盘风险计算器</div>
                            <Text type="secondary">实时计算你的最大潜在损失</Text>
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '1.25rem',
                            marginBottom: '1.25rem'
                        }}>
                            <div>
                                <Text type="secondary"
                                      style={{fontSize: '0.8rem', marginBottom: '.4rem', display: 'block'}}>
                                    账户总资金 (USDT)
                                </Text>
                                <InputNumber
                                    value={riskCapital}
                                    onChange={(val) => setRiskCapital(val || 0)}
                                    style={{
                                        width: '100%',
                                        background: 'var(--pt-bg-card)',
                                        border: '1px solid var(--pt-border)',
                                        color: 'var(--pt-text-primary)',
                                        padding: '.65rem 1rem',
                                        borderRadius: 8,
                                        fontSize: '0.9rem',
                                        fontFamily: "'IBM Plex Mono', monospace"
                                    }}
                                />
                            </div>
                            <div>
                                <Text type="secondary"
                                      style={{fontSize: '0.8rem', marginBottom: '.4rem', display: 'block'}}>
                                    最大回撤 (%)
                                </Text>
                                <InputNumber
                                    value={riskDrawdown}
                                    onChange={(val) => setRiskDrawdown(val || 0)}
                                    style={{
                                        width: '100%',
                                        background: 'var(--pt-bg-card)',
                                        border: '1px solid var(--pt-border)',
                                        color: 'var(--pt-text-primary)',
                                        padding: '.65rem 1rem',
                                        borderRadius: 8,
                                        fontSize: '0.9rem',
                                        fontFamily: "'IBM Plex Mono', monospace"
                                    }}
                                />
                            </div>
                            <div>
                                <Text type="secondary"
                                      style={{fontSize: '0.8rem', marginBottom: '.4rem', display: 'block'}}>
                                    策略资金占比 (%)
                                </Text>
                                <InputNumber
                                    value={riskAlloc}
                                    onChange={(val) => setRiskAlloc(val || 0)}
                                    style={{
                                        width: '100%',
                                        background: 'var(--pt-bg-card)',
                                        border: '1px solid var(--pt-border)',
                                        color: 'var(--pt-text-primary)',
                                        padding: '.65rem 1rem',
                                        borderRadius: 8,
                                        fontSize: '0.9rem',
                                        fontFamily: "'IBM Plex Mono', monospace"
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem'}}>
                            <RiskStatCard label="策略参与资金" value={riskExposed.toFixed(0)} suffix="USDT"/>
                            <RiskStatCard
                                label="最坏情景亏损"
                                value={riskMaxLoss.toFixed(0)}
                                suffix="USDT"
                                type="loss"
                            />
                            <RiskStatCard
                                label="最坏后剩余"
                                value={riskRemaining.toFixed(0)}
                                suffix="USDT"
                                type="safe"
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

/**
 * KPI卡片组件
 */
interface KpiCardProps {
    label: string;
    value: string;
    suffix?: string;
    borderColor?: string;
    change?: React.ReactNode;
    changeColor?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({label, value, suffix, borderColor, change, changeColor}) => (
    <div className={styles.kpi} style={{borderLeft: `2px solid ${borderColor || 'var(--pt-bull)'}`}}>
        <div className={styles['kpi-label']}>{label}</div>
        <div className={styles['kpi-val']} style={{fontFamily: "'IBM Plex Mono', monospace"}}>
            {value} {suffix && <span style={{fontSize: '.82rem', color: 'var(--pt-text-dim)'}}>{suffix}</span>}
        </div>
        {change && (
            <div className={`${styles['kpi-change']} ${changeColor ? styles[changeColor] : ''}`}>
                {change}
            </div>
        )}
    </div>
);

/**
 * 迷你统计组件
 */
interface MiniStatProps {
    label: string;
    value: string;
    color?: string;
}

const MiniStat: React.FC<MiniStatProps> = ({label, value, color}) => (
    <div style={{textAlign: 'center'}}>
        <div style={{fontSize: '0.72rem', color: 'var(--pt-text-dim)'}}>{label}</div>
        <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '0.9rem',
            color: color || 'var(--pt-text-primary)'
        }}>
            {value}
        </div>
    </div>
);

/**
 * 风险统计卡片组件
 */
interface RiskStatCardProps {
    label: string;
    value: string;
    suffix: string;
    type?: 'normal' | 'loss' | 'safe';
}

interface DetailItemProps {
    label: string;
    value: React.ReactNode;
}

const DetailItem: React.FC<DetailItemProps> = ({label, value}) => (
    <div className={styles.detailItem}>
        <div className={styles.detailLabel}>{label}</div>
        <div className={styles.detailValue}>{value}</div>
    </div>
);

const RiskStatCard: React.FC<RiskStatCardProps> = ({label, value, suffix, type = 'normal'}) => {
    const bgMap = {
        normal: 'var(--pt-bg-card)',
        loss: 'var(--pt-bear-dim)',
        safe: 'var(--pt-bull-dim)'
    };
    const colorMap = {
        normal: 'var(--pt-text-primary)',
        loss: 'var(--pt-bear)',
        safe: 'var(--pt-bull)'
    };

    return (
        <div style={{
            background: bgMap[type],
            border: `1px solid ${type === 'normal' ? 'var(--pt-border-subtle)' : 'rgba(255,107,107,0.2)'}`,
            borderRadius: 10,
            padding: '1rem',
            textAlign: 'center'
        }}>
            <div style={{fontSize: '0.75rem', color: 'var(--pt-text-dim)', marginBottom: '.4rem'}}>{label}</div>
            <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '1.2rem',
                fontWeight: 500,
                color: colorMap[type]
            }}>
                {value}
            </div>
            <div style={{fontSize: '0.72rem', color: 'var(--pt-text-dim)', marginTop: '.25rem'}}>{suffix}</div>
        </div>
    );
};

/**
 * 解析月度收益率数据
 */
function parseMonthlyReturns(tasks: StrategyTask[]): { month: string; value: number }[] {
    const months = ['10月', '9月', '8月', '7月', '6月'];
    const values = [4.2, 3.1, -0.9, 5.8, 3.5];
    return months.map((month, idx) => ({month, value: values[idx]}));
}

export default StrategyTasksPage;
